/**
 * Панель добавления новых каналов в запущенную РК.
 * UX-принцип: тот же, что на шаге «Обзор» в визарде —
 * каждый блогер отображается как BloggerGroup с настройкой
 * формата и варианта креатива. UTM-ссылка формируется автоматически
 * бэкендом при добавлении (см. app/utils/utm.py) — редактируется
 * позже, по одному размещению, на канбане.
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Calendar, ExternalLink, Eye, Loader2, Plus, Search, Shuffle, Users, X } from "lucide-react";
import {
  useBloggers, useCampaignCreatives, useExpandCampaign,
  type Blogger, type BloggerExpandConfig,
} from "@/shared/api/wb";
import { Avatar, NicheChips, PlatformIcon, type Platform } from "@/shared/ui";
import { fmtNum, fmtRub, fmtRubFull } from "@/shared/lib/format";
import { cn } from "@/shared/lib/cn";
import { sortNiches } from "@/pages/Catalog/engine";
import {
  getFormats, PLATFORM_LABELS,
  type CartSelection, type PlatformKey, type FormatType,
} from "@/features/blogger-card";
import { TelegramPost } from "../CampaignWizard/TelegramPost";

// ------------------------------------------------------------------
// Types
// ------------------------------------------------------------------

type LocalFormat = CartSelection;

type LocalState = {
  fmts: LocalFormat[];
  variantId: string;
  date: string;
  dateTo: string;
};

// ------------------------------------------------------------------
// Main panel
// ------------------------------------------------------------------

export function AddChannelsPanel({
  campaignId,
  campaignProduct,
  campaignType,
  existingBloggerIds,
  initialSelected = [],
  panelTitle,
  onClose,
  onAdded,
}: {
  campaignId: string;
  campaignProduct: string;
  campaignType: "seeding" | "native";
  existingBloggerIds: string[];
  initialSelected?: string[];
  panelTitle?: string;
  onClose: () => void;
  /** Called right before onClose once the add succeeds, so the parent can
   * surface a toast that survives this panel unmounting. */
  onAdded?: (message: string) => void;
}) {
  const navigate = useNavigate();
  const { data: bloggers } = useBloggers({});
  const { data: creativesData } = useCampaignCreatives(campaignId);
  const expand = useExpandCampaign(campaignId);

  const fromRecommendations = initialSelected.length > 0;

  const [search, setSearch] = useState("");
  const [platformFilter, setPlatformFilter] = useState<string>("all");
  const [nicheFilter, setNicheFilter] = useState<string[]>([]);
  const toggleNiche = (n: string) => setNicheFilter((s) => s.includes(n) ? s.filter((x) => x !== n) : [...s, n]);
  const [selected, setSelected] = useState<string[]>(initialSelected);
  const [state, setState] = useState<Record<string, LocalState>>(
    Object.fromEntries(initialSelected.map((id) => [id, { fmts: [], variantId: "", date: "", dateTo: "" }]))
  );
  const [showCatalog, setShowCatalog] = useState(false);
  const [previewVariantId, setPreviewVariantId] = useState<string | null>(null);
  const [bulkDate, setBulkDate] = useState("");
  const [bulkDateTo, setBulkDateTo] = useState("");
  const [bulkPeriodMode, setBulkPeriodMode] = useState(false);

  const allBloggers = bloggers ?? [];
  const variants = creativesData?.variants ?? [];
  const defaultVariantId = variants[0]?.id ?? "";
  const isNative = campaignType === "native";

  const catalog = allBloggers.filter((b) => {
    if (existingBloggerIds.includes(b.id)) return false;
    if (platformFilter !== "all" && b.platform !== platformFilter) return false;
    if (nicheFilter.length > 0 && !nicheFilter.some((n) => b.cats.includes(n))) return false;
    if (search.trim()) {
      const q = search.toLowerCase();
      return b.name.toLowerCase().includes(q) || b.full.toLowerCase().includes(q);
    }
    return true;
  });

  const selectedBloggers = allBloggers.filter((b) => selected.includes(b.id));
  const platforms = ["all", ...new Set(allBloggers.map((b) => b.platform))];
  const availableNiches = sortNiches([...new Set(
    allBloggers.filter((b) => platformFilter === "all" || b.platform === platformFilter).flatMap((b) => b.cats)
  )]);

  function toggle(id: string) {
    setSelected((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      setState((s) => ({
        ...s,
        [id]: s[id] ?? { fmts: [], variantId: defaultVariantId, date: "", dateTo: "" },
      }));
      return [...prev, id];
    });
  }

  function patchState(bloggerId: string, patch: Partial<LocalState>) {
    setState((s) => ({ ...s, [bloggerId]: { ...s[bloggerId], ...patch } }));
  }

  function distributeVariants() {
    if (variants.length < 2) return;
    const shuffled = [...variants].sort(() => Math.random() - 0.5);
    selectedBloggers.forEach((b, i) => {
      patchState(b.id, { variantId: shuffled[i % shuffled.length].id });
    });
  }

  function applyBulkDate() {
    if (!bulkDate) return;
    const dateTo = bulkPeriodMode ? (bulkDateTo || bulkDate) : "";
    selectedBloggers.forEach((b) => patchState(b.id, { date: bulkDate, dateTo }));
  }

  const missingDatesCount = selectedBloggers.filter((b) => !state[b.id]?.date).length;

  async function handleAdd() {
    if (selected.length === 0 || expand.isPending || missingDatesCount > 0) return;
    const configs: BloggerExpandConfig[] = selected.map((bid) => {
      const s = state[bid];
      const fmt = s?.fmts[0];
      return {
        blogger_id: bid,
        placement_type: fmt?.type ?? (isNative ? "nativ" : "posev"),
        format_id: fmt?.formatId,
        format_name: fmt?.formatName,
        variant_id: s?.variantId || defaultVariantId || undefined,
        date: s?.date || undefined,
        date_to: s?.dateTo || undefined,
      };
    });
    await expand.mutateAsync({ blogger_ids: selected, configs });
    const count = selected.length;
    onAdded?.(`${count} ${pluralChannels(count)} добавлено · ${fmtRubFull(totalCost)} спишется, когда согласуете даты выхода`);
    onClose();
  }

  const totalCost = selectedBloggers.reduce((s, b) => {
    const fmts = state[b.id]?.fmts;
    return s + (fmts?.length ? fmts.reduce((n, f) => n + f.price, 0) : b.price);
  }, 0);

  const previewVariant = previewVariantId ? variants.find((v) => v.id === previewVariantId) : null;

  return (
    <div
      className="fixed inset-0 z-[80] grid place-items-center p-6"
      style={{ background: "rgba(28,28,28,0.50)", backdropFilter: "blur(6px)" }}
      onClick={onClose}
    >
      <div
        className="bg-[var(--bg)] rounded-xl shadow-xl flex flex-col fade-up"
        style={{ width: "min(780px, 96vw)", maxHeight: "min(88vh, 860px)" }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-ink-12 flex items-center justify-between flex-shrink-0">
          <div>
            <div className="text-[11px] font-display font-semibold uppercase tracking-[0.16em] text-ink-40 mb-0.5">
              {panelTitle ?? "Добавить каналы"} · {campaignProduct}
            </div>
            <h2 className="font-display font-extrabold text-[20px] tracking-tight leading-tight">
              {fromRecommendations ? "Настройте размещение" : "Выберите блогеров и настройте размещение"}
            </h2>
          </div>
          <button onClick={onClose} className="w-9 h-9 grid place-items-center rounded-md text-ink-60 hover:bg-ink-6 flex-shrink-0">
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto">
          {/* Catalog picker — hidden when opened from recommendations */}
          {(!fromRecommendations || showCatalog) && (
            <div className="px-6 pt-4 pb-3 border-b border-ink-12">
              <div className="text-[11px] font-display font-semibold uppercase tracking-[0.12em] text-ink-40 mb-3">
                Каталог блогеров
              </div>
              <div className="flex items-center gap-2 mb-3">
                <div className="relative flex-1">
                  <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-30 pointer-events-none" />
                  <input
                    type="text"
                    placeholder="Поиск по имени или каналу…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="w-full h-8 pl-8 pr-3 rounded-md border border-ink-12 bg-white text-[12.5px] outline-none focus:border-ink"
                  />
                </div>
                <div className="flex gap-1">
                  {platforms.slice(0, 5).map((p) => (
                    <button
                      key={p}
                      onClick={() => setPlatformFilter(p)}
                      className={cn(
                        "h-8 px-3 rounded-pill text-[12px] font-display font-semibold transition-colors",
                        platformFilter === p ? "bg-ink text-white" : "bg-ink-6 text-ink-60 hover:text-ink",
                      )}
                    >
                      {p === "all" ? "Все" : p.toUpperCase()}
                    </button>
                  ))}
                </div>
              </div>
              <div className="mb-3">
                <NicheChips niches={availableNiches} selected={nicheFilter} onToggle={toggleNiche} />
              </div>
              <div className="grid gap-1 max-h-[240px] overflow-y-auto pr-1">
                {catalog.length === 0 && (
                  <div className="text-[12.5px] text-ink-40 text-center py-6">
                    <Users size={20} className="mx-auto mb-2 opacity-40" />
                    Нет блогеров по фильтру
                  </div>
                )}
                {catalog.map((b) => {
                  const isSelected = selected.includes(b.id);
                  return (
                    <button
                      key={b.id}
                      onClick={() => toggle(b.id)}
                      className={cn(
                        "flex items-center gap-3 px-3 py-2 rounded-md text-left transition-colors",
                        isSelected ? "bg-lime/10 border border-lime/30" : "hover:bg-ink-6 border border-transparent",
                      )}
                    >
                      <Avatar name={b.full} size={32} grad={b.grad as [string, string]} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 text-[13px] font-display font-semibold">
                          <PlatformIcon platform={b.platform as Platform} size={11} />
                          {b.name}
                        </div>
                        <div className="text-[11px] text-ink-40 truncate">
                          {b.full} · {fmtNum(b.reach)} · ~{fmtRub(b.price)}
                        </div>
                      </div>
                      <div className={cn(
                        "w-5 h-5 rounded-full border-2 grid place-items-center flex-shrink-0 transition-colors",
                        isSelected ? "bg-lime border-lime" : "border-ink-20",
                      )}>
                        {isSelected && <span className="text-white text-[9px] font-bold">✓</span>}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Configuration section */}
          {selectedBloggers.length > 0 && (
            <div className="px-6 pt-4 pb-3">
              <div className="flex items-center justify-between mb-3">
                <div className="text-[11px] font-display font-semibold uppercase tracking-[0.12em] text-ink-40">
                  Настройка · {selectedBloggers.length} {pluralChannels(selectedBloggers.length)}
                </div>
                {variants.length > 1 && !isNative && (
                  <button
                    onClick={distributeVariants}
                    className="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-pill border border-ink-12 text-[11.5px] font-display font-semibold text-ink-60 hover:text-ink hover:border-ink/40"
                  >
                    <Shuffle size={11} /> Распределить равномерно
                  </button>
                )}
              </div>

              {missingDatesCount > 0 && (
                <div className="flex items-center gap-2 px-3.5 py-2 mb-3 rounded-md bg-warning/10 border border-warning/30 text-[12px] text-ink">
                  <Calendar size={13} className="text-warning flex-shrink-0" />
                  Укажите дату размещения ещё для {missingDatesCount} {pluralChannels(missingDatesCount)} — без даты канал не добавится.
                </div>
              )}

              <div className="rounded-lg border border-ink-12 overflow-hidden bg-white">
                {/* Bulk date/period — set once, apply to every selected channel */}
                <div className="flex items-center gap-2.5 flex-wrap px-4 py-2.5 border-b border-ink-12 bg-ink-6/40">
                  <span className="inline-flex items-center gap-1.5 text-[11px] font-display font-semibold text-ink-60">
                    <Calendar size={12} /> Дата для всех каналов
                  </span>
                  <div className="flex items-center gap-1 p-0.5 rounded-pill bg-white border border-ink-12">
                    <button
                      type="button"
                      onClick={() => setBulkPeriodMode(false)}
                      className={cn(
                        "h-6 px-2.5 rounded-pill text-[11px] font-display font-semibold transition-colors",
                        !bulkPeriodMode ? "bg-ink-6 text-ink" : "text-ink-40 hover:text-ink",
                      )}
                    >
                      Точная дата
                    </button>
                    <button
                      type="button"
                      onClick={() => setBulkPeriodMode(true)}
                      className={cn(
                        "h-6 px-2.5 rounded-pill text-[11px] font-display font-semibold transition-colors",
                        bulkPeriodMode ? "bg-ink-6 text-ink" : "text-ink-40 hover:text-ink",
                      )}
                    >
                      Период
                    </button>
                  </div>
                  <input
                    type="date"
                    value={bulkDate}
                    onChange={(e) => setBulkDate(e.target.value)}
                    className="h-7 px-2 rounded-md border border-ink-12 bg-white text-[12px] font-mono outline-none focus:border-ink"
                  />
                  {bulkPeriodMode && (
                    <>
                      <span className="text-ink-40 text-[11px]">—</span>
                      <input
                        type="date"
                        value={bulkDateTo}
                        min={bulkDate || undefined}
                        onChange={(e) => setBulkDateTo(e.target.value)}
                        className="h-7 px-2 rounded-md border border-ink-12 bg-white text-[12px] font-mono outline-none focus:border-ink"
                      />
                    </>
                  )}
                  <button
                    type="button"
                    disabled={!bulkDate}
                    onClick={applyBulkDate}
                    className="inline-flex items-center gap-1.5 h-7 px-3 rounded-pill bg-ink text-white text-[11px] font-display font-semibold hover:bg-[#2A2A2A] disabled:opacity-40 disabled:cursor-not-allowed ml-auto"
                  >
                    Применить ко всем ({selectedBloggers.length})
                  </button>
                </div>

                {selectedBloggers.map((b, idx) => (
                  <BloggerGroup
                    key={b.id}
                    blogger={b}
                    localState={state[b.id] ?? { fmts: [], variantId: defaultVariantId, date: "", dateTo: "" }}
                    onChange={(patch) => patchState(b.id, patch)}
                    variants={variants}
                    isNative={isNative}
                    onPreviewCreative={setPreviewVariantId}
                    onRemove={() => toggle(b.id)}
                    isLast={idx === selectedBloggers.length - 1}
                  />
                ))}
              </div>

              {/* "Show more similar" — only in recommendation mode, catalog not yet open */}
              {fromRecommendations && !showCatalog && (
                <button
                  onClick={() => setShowCatalog(true)}
                  className="mt-3 w-full flex items-center justify-center gap-2 h-9 rounded-pill border border-dashed border-ink-20 text-[13px] font-display font-semibold text-ink-60 hover:text-ink hover:border-ink/60 transition-colors"
                >
                  <Search size={13} /> Показать ещё похожих
                </button>
              )}
            </div>
          )}

          {selectedBloggers.length === 0 && !fromRecommendations && (
            <div className="px-6 py-10 text-center text-ink-40 text-[13px]">
              Выберите блогеров из каталога выше — здесь появятся настройки
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-ink-12 flex items-center justify-between flex-shrink-0 bg-white rounded-b-xl">
          <div className="text-[13px] text-ink-60">
            {selected.length > 0
              ? missingDatesCount > 0
                ? <span className="text-warning font-semibold">Укажите дату размещения для всех каналов</span>
                : <><b className="text-ink">{selected.length}</b> {pluralChannels(selected.length)} · {fmtRubFull(totalCost)}</>
              : "Не выбрано каналов"}
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => navigate("/catalog", { state: { preselectedCampaignId: campaignId } })}
              className="inline-flex items-center gap-1.5 h-9 px-4 rounded-pill border-2 border-ink-12 text-[13px] font-display font-semibold text-ink-60 hover:border-ink hover:text-ink"
            >
              <ExternalLink size={14} /> Перейти в каталог
            </button>
            <button
              onClick={onClose}
              className="inline-flex items-center h-9 px-4 rounded-pill border border-ink-12 text-[13px] font-display font-semibold text-ink-60 hover:text-ink hover:border-ink/40"
            >
              Отмена
            </button>
            <button
              onClick={handleAdd}
              disabled={selected.length === 0 || expand.isPending || missingDatesCount > 0}
              className="inline-flex items-center gap-1.5 h-9 px-5 rounded-pill bg-lime text-[var(--fg-on-accent)] font-display font-semibold text-[13px] disabled:opacity-50 disabled:cursor-not-allowed hover:[background:color-mix(in_srgb,var(--wb-lime)_88%,black)]"
            >
              {expand.isPending
                ? <><Loader2 size={13} className="animate-spin" /> Добавляем…</>
                : <><Plus size={13} strokeWidth={3} /> Добавить {selected.length > 0 ? selected.length : ""} {selected.length > 0 ? pluralChannels(selected.length) : "каналы"}</>}
            </button>
          </div>
        </div>
      </div>

      {/* Creative preview modal */}
      {previewVariant && (
        <div
          className="fixed inset-0 z-[120] grid place-items-center p-6"
          style={{ background: "rgba(28,28,28,0.55)", backdropFilter: "blur(6px)" }}
          onClick={() => setPreviewVariantId(null)}
        >
          <div
            className="bg-[var(--bg)] rounded-lg shadow-lg flex flex-col"
            style={{ width: "min(560px, 96vw)", maxHeight: "min(88vh, 800px)" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-3.5 border-b border-ink-12 flex-shrink-0">
              <div className="font-display font-bold text-[16px]">{previewVariant.name}</div>
              <button onClick={() => setPreviewVariantId(null)} className="w-8 h-8 grid place-items-center rounded-md hover:bg-ink-6">
                <X size={16} />
              </button>
            </div>
            <div className="overflow-y-auto flex-1 min-h-0 p-5 grid gap-4 content-start">
              <TelegramPost p={{ name: previewVariant.name, text: previewVariant.text, media: previewVariant.media, link: previewVariant.link }} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ------------------------------------------------------------------
// BloggerGroup — same visual as ReviewStep
// ------------------------------------------------------------------

function BloggerGroup({
  blogger, localState, onChange, variants, isNative,
  onPreviewCreative, onRemove, isLast,
}: {
  blogger: Blogger;
  localState: LocalState;
  onChange: (patch: Partial<LocalState>) => void;
  variants: { id: string; name: string; text: string; link: string; media: { id: string; kind: "image"|"video"; url: string }[] }[];
  isNative: boolean;
  onPreviewCreative: (vid: string) => void;
  onRemove: () => void;
  isLast: boolean;
}) {
  const { fmts, variantId, date, dateTo } = localState;
  const [periodMode, setPeriodMode] = useState(!!dateTo);
  // External changes (bulk "apply to all") can set dateTo without this row
  // ever having toggled locally — show the range fields either way.
  const showPeriod = periodMode || !!dateTo;

  const effectiveVariantId = variantId || variants[0]?.id || "";

  // Default row: one format based on platform
  const defaultType = isNative ? "nativ" : "posev";
  const rows: CartSelection[] = fmts.length > 0
    ? fmts
    : [{ type: defaultType, platform: blogger.platform as PlatformKey, formatId: "", formatName: "—", price: blogger.price, cpv: blogger.cpv, placementDate: "" }];

  function updateFormat(platform: string, type: string, entry: { id: string; name: string; price: number; cpv: number }) {
    const updated = rows.map((r) =>
      r.platform === platform && r.type === type
        ? { ...r, formatId: entry.id, formatName: entry.name, price: entry.price, cpv: entry.cpv }
        : r
    );
    onChange({ fmts: updated });
  }

  return (
    <div className={cn("border-b border-ink-12/60", isLast && "border-b-0")}>
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-2.5 bg-ink-6/50">
        <Avatar name={blogger.full} size={30} grad={blogger.grad as [string, string]} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 font-display font-semibold text-[13px]">
            <PlatformIcon platform={blogger.platform as Platform} size={11} />
            {blogger.name}
          </div>
          <div className="text-[10.5px] text-ink-40 truncate">{blogger.full}</div>
        </div>
        <div className="text-[11px] text-ink-40 font-mono">{fmtNum(blogger.reach)} просм.</div>
        <button
          onClick={onRemove}
          className="inline-flex items-center gap-1 h-6 px-2 rounded-pill border border-ink-12 text-[11px] font-display font-semibold text-ink-40 hover:text-pink hover:border-pink/40"
        >
          <X size={10} strokeWidth={2.5} /> Убрать
        </button>
      </div>

      {/* Placement date/period — required, sent to the blogger with the creative */}
      <div className="flex items-center gap-2 flex-wrap px-4 py-2 border-t border-ink-12/40 bg-white">
        <span className="inline-flex items-center gap-1.5 text-[11px] font-display font-semibold text-ink-60">
          <Calendar size={12} className={cn(!date && "text-warning")} /> Дата размещения
        </span>
        <div className="flex items-center gap-1 p-0.5 rounded-pill bg-ink-6">
          <button
            type="button"
            onClick={() => { setPeriodMode(false); onChange({ date, dateTo: "" }); }}
            className={cn(
              "h-5 px-2 rounded-pill text-[10.5px] font-display font-semibold transition-colors",
              !showPeriod ? "bg-white shadow-sm text-ink" : "text-ink-40 hover:text-ink",
            )}
          >
            Точная дата
          </button>
          <button
            type="button"
            onClick={() => { setPeriodMode(true); onChange({ date, dateTo: dateTo || date }); }}
            className={cn(
              "h-5 px-2 rounded-pill text-[10.5px] font-display font-semibold transition-colors",
              showPeriod ? "bg-white shadow-sm text-ink" : "text-ink-40 hover:text-ink",
            )}
          >
            Период
          </button>
        </div>
        <input
          type="date"
          required
          value={date}
          onChange={(e) => onChange({ date: e.target.value, dateTo: showPeriod ? (dateTo || e.target.value) : "" })}
          className={cn(
            "h-7 px-2 rounded-md border bg-white text-[11.5px] font-mono outline-none focus:border-ink",
            date ? "border-ink-12" : "border-warning",
          )}
        />
        {showPeriod && (
          <>
            <span className="text-ink-40 text-[11px]">—</span>
            <input
              type="date"
              required
              min={date || undefined}
              value={dateTo}
              onChange={(e) => onChange({ dateTo: e.target.value })}
              className="h-7 px-2 rounded-md border border-ink-12 bg-white text-[11.5px] font-mono outline-none focus:border-ink"
            />
          </>
        )}
        {!date && (
          <span className="text-[10.5px] text-warning font-display font-semibold">Без даты канал не добавится</span>
        )}
      </div>

      {/* Format + creative row */}
      {rows.map((fmt) => (
        <FormatRow
          key={`${fmt.platform}::${fmt.type}`}
          fmt={fmt}
          variants={variants}
          assignedVariantId={effectiveVariantId}
          onPickVariant={(vid) => onChange({ variantId: vid })}
          onPreviewCreative={onPreviewCreative}
          onUpdateFormat={updateFormat}
        />
      ))}

    </div>
  );
}

// ------------------------------------------------------------------
// FormatRow — same as ReviewStep
// ------------------------------------------------------------------

function FormatRow({
  fmt, variants, assignedVariantId,
  onPickVariant, onPreviewCreative, onUpdateFormat,
}: {
  fmt: CartSelection;
  variants: { id: string; name: string }[];
  assignedVariantId: string;
  onPickVariant: (vid: string) => void;
  onPreviewCreative: (vid: string) => void;
  onUpdateFormat: (platform: string, type: string, entry: { id: string; name: string; price: number; cpv: number }) => void;
}) {
  const availableFormats = getFormats(fmt.platform as PlatformKey, fmt.type as FormatType);
  const platformLabel = PLATFORM_LABELS[fmt.platform as PlatformKey] ?? fmt.platform.toUpperCase();
  const isPosev = fmt.type === "posev";
  const typeColors = isPosev
    ? { bg: "bg-lime/20", text: "text-ink" }
    : { bg: "bg-[#E6F1FB]", text: "text-[#185FA5]" };

  return (
    <div className="grid grid-cols-[auto_1fr_80px_1fr] gap-3 items-center px-4 py-2 border-t border-ink-12/40 bg-white">
      <div className="flex items-center gap-1.5">
        <PlatformIcon platform={fmt.platform as Platform} size={12} />
        <span className={cn("inline-flex items-center h-5 px-2 rounded-pill text-[10px] font-display font-bold", typeColors.bg, typeColors.text)}>
          {isPosev ? "Посев" : "Натив"} · {platformLabel}
        </span>
      </div>

      {availableFormats.length > 1 ? (
        <select
          value={fmt.formatId}
          onChange={(e) => {
            const entry = availableFormats.find((f) => f.id === e.target.value);
            if (entry) onUpdateFormat(fmt.platform, fmt.type, entry);
          }}
          className="h-7 px-2 rounded-md border border-ink-12 bg-white text-[12px] font-display font-semibold outline-none focus:border-ink"
        >
          {availableFormats.map((f) => (
            <option key={f.id} value={f.id}>{f.name}{f.desc ? ` · ${f.desc}` : ""}</option>
          ))}
        </select>
      ) : (
        <div className="text-[12px] font-display font-semibold text-ink-60">{fmt.formatName || "—"}</div>
      )}

      <div className="font-mono font-bold text-[12px] tabular-nums">{fmtRub(fmt.price)}</div>

      <div className="flex items-center gap-1.5">
        {variants.length > 1 ? (
          <select
            value={assignedVariantId}
            onChange={(e) => onPickVariant(e.target.value)}
            className="flex-1 h-7 px-2 rounded-md border border-ink-12 bg-white text-[12px] font-display font-semibold outline-none focus:border-ink"
          >
            {variants.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
          </select>
        ) : (
          <div className="flex-1 text-[12px] font-display font-semibold text-ink-60 truncate">
            {variants[0]?.name ?? "Без варианта"}
          </div>
        )}
        {assignedVariantId && (
          <button
            onClick={() => onPreviewCreative(assignedVariantId)}
            title="Посмотреть креатив"
            className="w-7 h-7 grid place-items-center rounded-md border border-ink-12 text-ink-40 hover:text-ink hover:border-ink/40 flex-shrink-0"
          >
            <Eye size={13} />
          </button>
        )}
      </div>
    </div>
  );
}

// ------------------------------------------------------------------
// Helpers
// ------------------------------------------------------------------

function pluralChannels(n: number): string {
  if (n % 10 === 1 && n % 100 !== 11) return "канал";
  if ([2, 3, 4].includes(n % 10) && ![12, 13, 14].includes(n % 100)) return "канала";
  return "каналов";
}
